import React, { useState } from "react";
import { Button, Flex, Form, Input, Modal, Typography, Alert } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";

const { Text, Paragraph } = Typography;

interface RateLimitModalProps {
  open: boolean;
  retryAfter?: number;
  countdownSeconds?: number;
  limitType?: 'hourly' | 'daily';
  onClose: () => void;
  onSubmitEmail: (email: string) => Promise<void>;
}

const RateLimitModal: React.FC<RateLimitModalProps> = ({
  open,
  retryAfter,
  countdownSeconds,
  limitType,
  onClose,
  onSubmitEmail,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const formatRetryTime = (seconds?: number): string => {
    if (!seconds || seconds <= 0) return "soon";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    }
    if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? "s" : ""}`;
    }
    return "less than a minute";
  };

  const formatCountdown = (seconds: number | undefined): string => {
    if (!seconds || seconds <= 0) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleSubmit = async (values: { email: string }) => {
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      await onSubmitEmail(values.email);
      setSubmitStatus("success");
      form.resetFields();

      // Auto-close after showing success message
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;

      if (status === 400) {
        setErrorMessage(
          message || "Invalid email address. Please check and try again."
        );
      } else {
        setErrorMessage(
          "Unable to register your email. Please try again later."
        );
      }
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSubmitStatus("idle");
    setErrorMessage("");
    onClose();
  };

  return (
    <Modal
      title={
        <Flex align="center" gap={8}>
          <ClockCircleOutlined style={{ fontSize: 18, color: "#faad14" }} />
          <span>Rate Limit Reached</span>
        </Flex>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      centered
      width={480}
    >
      <div style={{ marginTop: 16 }}>
        <Paragraph>
          {limitType === 'daily'
            ? "You've used all your screening credits for today. To help us maintain quality service for everyone, we limit the number of screenings per day."
            : limitType === 'hourly'
            ? "You've reached your hourly screening limit. To help us maintain quality service for everyone, we limit the number of screenings per hour."
            : "You've reached the maximum number of screening requests for now. To help us maintain quality service for everyone, we limit the number of screenings per hour."}
        </Paragraph>

        {(countdownSeconds !== undefined || retryAfter) && (
          <Alert
            message={
              countdownSeconds !== undefined && countdownSeconds > 0
                ? `You can try again in ${formatCountdown(countdownSeconds)}`
                : retryAfter
                ? `You can try again in ${formatRetryTime(retryAfter)}`
                : 'You can try again soon'
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {submitStatus === "success" ? (
          <Alert
            message="Success!"
            description="We'll notify you when you can screen again."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Leave your email and we'll notify you when you can analyze more
              candidates:
            </Paragraph>

            {submitStatus === "error" && errorMessage && (
              <Alert
                message={errorMessage}
                type="error"
                showIcon
                closable
                onClose={() => setErrorMessage("")}
                style={{ marginBottom: 16 }}
              />
            )}

            <Form
              form={form}
              name="rateLimitNotifyForm"
              onFinish={handleSubmit}
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input
                  placeholder="your.email@example.com"
                  disabled={isSubmitting}
                />
              </Form.Item>

              <Flex align="center" justify="space-between" gap={16}>
                <Button
                  block
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Close
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Notify Me
                </Button>
              </Flex>
            </Form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RateLimitModal;
